import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ScaleIcon,
  HomeModernIcon,
  CalculatorIcon,
  DocumentCheckIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

/**
 * 2026 Irish Landlord & Tenant Rules Resource Page
 * Optimized for SEO and Agent Lead Generation
 */
const LandlordRules2026: React.FC = () => {
  const pageTitle =
    'Irish Landlord Rules 2026 | Guide for Agents & Property Owners';
  const description =
    'Comprehensive guide to the March 1, 2026 Irish rental reforms. Understand TMDs, National Rent Caps, Large vs Small Landlord rules, and BER mandates.';
  const canonicalUrl = 'https://rentsmart.ie/blog/irish-landlord-rules-2026'; // Adjusted domain

  // Data Objects for Iteration - FIXED & POPULATED
  const summaryItems = [
    {
      icon: ShieldCheckIcon,
      title: 'Tenant-in-Situ (TMD)',
      desc: "Tenancies of Unlimited Duration (TMD) become the national standard (6-year rolling terms). 'No-fault' evictions are severely restricted for all landlords.",
      color: 'text-emerald-500'
    },
    {
      icon: CalculatorIcon,
      title: 'National Rent Cap',
      desc: 'The entire country falls under a unified rent cap. Annual increases are restricted to the lower of the prevailing CPI rate or 2%. RPZ rules apply nationwide.',
      color: 'text-yellow-500'
    },
    {
      icon: HomeModernIcon,
      title: 'BER Mandates',
      desc: 'New energy efficiency minimums are phased in (D2 by 2028, C by 2030). Properties failing to upgrade will be barred from being rented out.',
      color: 'text-blue-500'
    }
  ];

  const energyRoadmap = [
    {
      year: '2026',
      target: 'BER Register Update',
      status:
        'Mandatory BER display on all listings and lease agreements for new tenancies from March 1st.'
    },
    {
      year: '2028',
      target: 'Minimum BER D2',
      status:
        'All rental properties must achieve a minimum D2 BER rating to be re-let or remain on the market by end of year.'
    },
    {
      year: '2030',
      target: 'Minimum BER C',
      status:
        'The standard is raised to a minimum C BER rating, significantly impacting older, uninsulated stock by end of year.'
    }
  ];

  const faqItems = [
    {
      q: "What is the new definition of a 'Small' landlord?",
      a: "A 'Small' landlord is defined as an individual or entity owning three or fewer residential tenancies nationwide. This status grants them slightly more flexibility regarding termination at the end of each 6-year TMD term."
    },
    {
      q: 'How does the National Rent Cap of CPI or 2% work?',
      a: 'Rent increases for all tenancies are limited to the lower of the annual Consumer Price Index (CPI) or 2%. For example, if CPI is 1.5%, the maximum increase is 1.5%; if CPI is 3%, the maximum increase is 2%. This applies nationwide.'
    },
    {
      q: "Can a 'Large' landlord (4+ properties) ever terminate a tenancy?",
      a: "Yes, but termination grounds are extremely limited, primarily to tenant non-compliance (e.g., severe rent arrears, property damage) or if the property is no longer suitable for the tenant's needs. Termination for sale or family use is banned."
    },
    {
      q: 'When can a landlord reset the rent to market value?',
      a: "Rent can be reset to market value (if below market) only when a tenancy ends due to: 1) The tenant leaving voluntarily, 2) The tenant breaching obligations, 3) The property no longer meeting the tenant's needs, or 4) At the end of a 6-year TMD cycle (provided no 'no-fault' eviction occurred)."
    }
  ];

  // JSON-LD Schema for SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <div className="bg-white relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visiblefont-sans text-slate-900 selection:bg-emerald-100">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content="Irish landlord rules 2026, TMD Ireland, RTB compliance, national rent cap Ireland, rental reform 2026, landlord-toolkit"
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content="https://rentsmart.ie/images/social/landlord-rules-2026.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Modern Gradient Hero */}
      <header className="relative bg-slate-900 pt-24 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center lg:text-left">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest leading-none">
              Regulatory Update 2026
            </span>
          </div>
          <h1 className="text-4xl md:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-8">
            Navigating the{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-200">
              2026 Shift
            </span>{' '}
            in Irish Renting
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mb-12 leading-relaxed">
            The March 1st deadline marks the most significant regulatory overhaul since 2004. For
            property agents and landlords, compliance is no longer an option—it is a survival
            requirement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link
              to="/landlord-toolkit"
              className="bg-emerald-500 text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-400 transition-all flex items-center justify-center shadow-lg shadow-emerald-500/20 no-underline"
            >
              Access Landlord Toolkit <ChevronRightIcon className="w-5 h-5 ml-2" />
            </Link>
            {/* <button className="bg-white/5 border border-white/10 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all backdrop-blur-sm">
              Book a demo 
            </button> */}
          </div>
        </div>
      </header>

      {/* Summary Matrix Cards */}
      <section className="max-w-7xl mx-auto px-6 -mt-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {summaryItems.map((item, i) => (
            <div
              key={i}
              className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 group hover:border-emerald-200 transition-colors"
            >
              <item.icon
                className={`w-12 h-12 ${item.color} mb-6 transition-transform group-hover:scale-110`}
              />
              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content Layout */}
      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* Content Body */}
          <div className="lg:w-2/3">
            <article className="prose prose-slate prose-lg max-w-none prose-headings:font-extrabold prose-headings:tracking-tight prose-a:text-emerald-600">
              <section id="landlord-types">
                <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-emerald-500 pl-4 mb-8">
                  The Landlord Binary: Small vs. Large
                </h2>
                <p>
                  Asset control in 2026 hinges on your portfolio size. The{' '}
                  <strong>'Small' landlord</strong> (≤3 tenancies) retains rights for family use and
                  sale under hardship, while the <strong>'Large' landlord</strong> (4+ tenancies or
                  company) moves to a pure 'tenant-in-situ' investment model.
                </p>

                <div className="my-10 bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="bg-slate-900 text-white px-6 py-4 font-bold flex items-center">
                    <ScaleIcon className="w-5 h-5 mr-2 text-emerald-400" /> Comparison of Rights
                    (New Tenancies)
                  </div>
                  <table className="w-full text-left border-collapse m-0">
                    <thead className="bg-slate-100 border-b border-slate-200">
                      <tr>
                        <th className="p-4 font-bold text-sm uppercase">Termination Ground</th>
                        <th className="p-4 font-bold text-sm uppercase">Small (≤3)</th>
                        <th className="p-4 font-bold text-sm uppercase">Large (4+)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr>
                        <td className="p-4 italic">Breach of Obligations</td>
                        <td className="p-4 text-emerald-600 font-bold">YES</td>
                        <td className="p-4 text-emerald-600 font-bold">YES</td>
                      </tr>
                      <tr>
                        <td className="p-4 italic">Intention to Sell</td>
                        <td className="p-4 text-slate-700">Yes (at term end)</td>
                        <td className="p-4 text-rose-600 font-bold uppercase">NO</td>
                      </tr>
                      <tr>
                        <td className="p-4 italic">Family/Personal Use</td>
                        <td className="p-4 text-emerald-600 font-bold">YES</td>
                        <td className="p-4 text-rose-600 font-bold uppercase">NO</td>
                      </tr>
                      <tr>
                        <td className="p-4 italic">Substantial Renovation</td>
                        <td className="p-4 text-slate-700">Yes (at term end)</td>
                        <td className="p-4 text-rose-600 font-bold uppercase">NO</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="rent-reviews" className="pt-12">
                <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-emerald-500 pl-4 mb-8">
                  Mathematics of the 2% Cap
                </h2>
                <p>
                  From March 2026, the geographic boundaries of Rent Pressure Zones dissolve. Every
                  square kilometer of Ireland falls under a unified cap. Rents are reviewed
                  annually, restricted to the <strong>lower of CPI or 2%</strong>.
                </p>

                <div className="bg-amber-50 border border-amber-200 p-8 rounded-2xl my-8">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-6 h-6 text-amber-600 mr-3 mt-1 shrink-0" />
                    <div>
                      <h4 className="text-amber-900 font-bold m-0">The 'Market Reset' Exception</h4>
                      <p className="text-amber-800 text-sm mt-2 mb-0">
                        Landlords can break the 2% cap and reset to full market rates only if: a
                        tenant leaves voluntarily, a breach occurs, or the 6-year TMD cycle ends.{' '}
                        <strong>Crucially:</strong> A reset is forbidden following a 'no-fault'
                        termination.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="energy-compliance" className="pt-12">
                <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-emerald-500 pl-4 mb-8">
                  The BER "Hard Stop" Timeline
                </h2>
                <p>
                  Sustainability is now a legal standard. Under the EPBD transposition, rental
                  properties face a phased removal from the market if they fail to upgrade energy
                  efficiency.
                </p>
                <div className="space-y-4 my-8">
                  {energyRoadmap.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center bg-white border border-slate-200 p-4 rounded-xl shadow-sm"
                    >
                      <div className="w-16 h-16 rounded-lg bg-slate-900 text-emerald-400 flex flex-col items-center justify-center shrink-0">
                        <span className="text-xs uppercase font-bold">End of</span>
                        <span className="text-xl font-black">{item.year}</span>
                      </div>
                      <div className="ml-6">
                        <div className="font-extrabold text-slate-900">{item.target}</div>
                        <div className="text-sm text-slate-500">{item.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* High SEO Value FAQ Section */}
              <section className="pt-24 border-t border-slate-100">
                <h2 className="text-4xl font-black text-slate-900 mb-12">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-8">
                  {faqItems.map((item, i) => (
                    <div key={i}>
                      <h4 className="text-xl font-bold text-slate-900">{item.q}</h4>
                      <p className="text-slate-600">{item.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </article>
          </div>

          {/* Sticky Sidebar CTA */}
          <aside className="lg:w-1/3">
            <div className="sticky top-12 space-y-8">
              <div className="bg-emerald-50 p-8 rounded-3xl border border-emerald-100 shadow-xl shadow-emerald-500/5">
                <h3 className="text-2xl font-black text-emerald-900 mb-6">2026 Agent Checklist</h3>
                <ul className="space-y-5 text-emerald-900/80">
                  <li className="flex items-start">
                    <DocumentCheckIcon className="w-5 h-5 mr-3 mt-1 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium">
                      Audit portfolio for 'Large' vs 'Small' status
                    </span>
                  </li>
                  <li className="flex items-start">
                    <DocumentCheckIcon className="w-5 h-5 mr-3 mt-1 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium">
                      Identify assets with BER ratings below D2
                    </span>
                  </li>
                  <li className="flex items-start">
                    <DocumentCheckIcon className="w-5 h-5 mr-3 mt-1 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium">
                      Update lease templates for CPI review clauses
                    </span>
                  </li>
                  <li className="flex items-start">
                    <DocumentCheckIcon className="w-5 h-5 mr-3 mt-1 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium">
                      Plan 'Market Reset' windows for current leases
                    </span>
                  </li>
                </ul>
                <Link
                  to="/landlord-toolkit"
                  className="no-underline mt-10 w-full bg-emerald-600 text-white text-center py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 transition-all block shadow-lg shadow-emerald-600/20"
                >
                  Access Landlord Toolkit
                </Link>
              </div>

              <div className="bg-slate-900 p-8 rounded-3xl text-white">
                <h4 className="text-lg font-bold mb-4">RTB Penalty Alert</h4>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  With a 70% budget increase to €22.8m, the RTB is moving to data-driven proactive
                  enforcement. Improper rent setting can now trigger fines of up to €15,000 per
                  breach.
                </p>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10 italic text-xs text-slate-400">
                  "The days of passive/amateur management are numbered." - industry consensus for
                  2026.
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Modern Footer with Disclaimer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="text-slate-400 text-sm leading-relaxed">
            <h5 className="text-slate-900 font-bold mb-4">Regulatory Disclaimer</h5>
            <p className="mb-4">
              The information provided herein summarizes the General Scheme of the Residential
              Tenancies (Amendment) (No. 2) Bill 2025. This legislation is subject to Dáil debate
              and official enactment.
            </p>
            <p>
              Portions of the Building Energy Rating (BER) roadmap are based on EU Directives (EPBD
              2024/1275) and local implementation proposals as of December 30, 2025. Always consult
              legal counsel.
            </p>
          </div>
          <div className="flex items-center md:justify-end">
            <div className="text-right">
              <p className="text-slate-900 font-bold mb-2">Need a software to manage your properties ?</p>
              <a
                href="mailto:expert@rentsmart.ie"
                className="text-emerald-600 font-black text-xl hover:underline"
              >
                support@rentsmart.ie
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandlordRules2026;
